import React, { useState, useRef } from 'react';
import api from '../api';

export default function AIFlowGenerator({ bot, onFlowCreated, onClose }) {
  const [step, setStep] = useState(1); // 1: form, 2: loading, 3: done
  const [description, setDescription] = useState('');
  const [themeColor, setThemeColor] = useState('#8B5CF6');
  const [images, setImages] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const EXAMPLE_PROMPTS = [
    'Kripto para haber botu. Günlük BTC/ETH fiyatları, piyasa haberleri, alarm kurma özelliği. Kullanıcılar coin seçip takip edebilsin.',
    'İngilizce kelime öğreten quiz botu. Günde 10 kelime, doğru/yanlış geri bildirimi, XP sistemi, seviye atlama.',
    'Restoran sipariş botu. Menü kategorileri, ürün seçimi, sipariş özeti, adres girişi, onay mesajı.',
    'Müşteri destek botu. Sık sorulan sorular, ticket oluşturma, canlı destek yönlendirme.',
    'Fitness antrenman botu. Hedef seçimi, günlük program, egzersiz açıklamaları, ilerleme takibi.',
  ];

  function pickImage(e) {
    const files = Array.from(e.target.files);
    setImages(prev => [...prev, ...files].slice(0, 8));
    e.target.value = '';
  }

  function removeImage(i) {
    setImages(prev => prev.filter((_, idx) => idx !== i));
  }

  async function generate() {
    if (!description.trim()) return setError('Bot açıklaması gerekli');
    setError('');
    setStep(2);

    try {
      const fd = new FormData();
      fd.append('bot_id', bot.id);
      fd.append('bot_name', bot.name);
      fd.append('description', description);
      fd.append('theme_color', themeColor);
      images.forEach(img => fd.append('images', img));

      const r = await api.post('/ai-flow/generate', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });

      setResult(r.data);
      setStep(3);
      if (onFlowCreated) onFlowCreated(r.data.flow);
    } catch(ex) {
      setError('❌ ' + (ex.response?.data?.error || ex.message));
      setStep(1);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border3)', borderRadius: 'var(--radius)', width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', padding: 28 }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, fontFamily: 'Plus Jakarta Sans', margin: 0 }}>🤖 AI ile Flow Oluştur</h2>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>Bot: <b>{bot.name}</b></div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 20 }}>✕</button>
        </div>

        {/* Step 1: Form */}
        {step === 1 && (
          <div>
            {error && <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13, marginBottom: 16 }}>{error}</div>}

            {/* Açıklama */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', display: 'block', marginBottom: 8 }}>
                📝 Bot ne yapacak? <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                className="input"
                value={description}
                onChange={e => setDescription(e.target.value)}
                style={{ minHeight: 120, fontSize: 13, resize: 'vertical' }}
                placeholder="Örnek: Türkçe dil öğreten bir bot. Günlük kelime öğretsin, quiz yapsın, kullanıcı ilerlemesini takip etsin. Başlangıç, orta ve ileri seviye olsun..."
              />
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>Ne kadar detaylı yazarsan, o kadar iyi bot çıkar.</div>
            </div>

            {/* Örnek promptlar */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: .5 }}>⚡ Örnek Promptlar</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {EXAMPLE_PROMPTS.map((p, i) => (
                  <button key={i} onClick={() => setDescription(p)}
                    style={{ textAlign: 'left', background: 'var(--surface2)', border: '1px solid var(--border3)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>
                    {p.slice(0, 80)}...
                  </button>
                ))}
              </div>
            </div>

            {/* Tema rengi */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', display: 'block', marginBottom: 8 }}>🎨 Tema Rengi</label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input type="color" value={themeColor} onChange={e => setThemeColor(e.target.value)} style={{ width: 48, height: 36, borderRadius: 8, border: '1px solid var(--border3)', cursor: 'pointer', padding: 2 }}/>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['#8B5CF6','#3B82F6','#10B981','#F59E0B','#EF4444','#EC4899','#06B6D4'].map(c => (
                    <button key={c} onClick={() => setThemeColor(c)}
                      style={{ width: 28, height: 28, borderRadius: '50%', background: c, border: themeColor === c ? '3px solid white' : '2px solid transparent', cursor: 'pointer', boxShadow: themeColor === c ? `0 0 0 2px ${c}` : 'none' }}/>
                  ))}
                </div>
              </div>
            </div>

            {/* Görseller */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', display: 'block', marginBottom: 8 }}>
                🖼️ Görseller <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 'normal' }}>(opsiyonel, max 8)</span>
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {images.map((img, i) => (
                  <div key={i} style={{ position: 'relative', width: 72, height: 72 }}>
                    <img src={URL.createObjectURL(img)} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border3)' }}/>
                    <button onClick={() => removeImage(i)} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                  </div>
                ))}
                {images.length < 8 && (
                  <button onClick={() => fileRef.current.click()}
                    style={{ width: 72, height: 72, borderRadius: 8, border: '2px dashed var(--border)', background: 'var(--surface2)', cursor: 'pointer', color: 'var(--text3)', fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={pickImage}/>
              {images.length > 0 && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>AI görselleri uygun node'lara otomatik ekleyecek.</div>}
            </div>

            {/* Buton */}
            <button onClick={generate} className="btn btn-primary" style={{ width: '100%', padding: '14px', fontSize: 15, fontWeight: 700 }}>
              🚀 Flow Oluştur
            </button>
          </div>
        )}

        {/* Step 2: Loading */}
        {step === 2 && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 64, marginBottom: 20, animation: 'spin 2s linear infinite', display: 'inline-block' }}>🤖</div>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>AI Flow Üretiyor...</h3>
            <p style={{ color: 'var(--text3)', fontSize: 13 }}>Gemini açıklamanı analiz ediyor ve bot flow'unu tasarlıyor.<br/>Bu 10-30 saniye sürebilir.</p>
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center', gap: 6 }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', animation: `bounce 1s ${i*0.2}s infinite`, opacity: .7 }}/>
              ))}
            </div>
            <style>{`@keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }`}</style>
          </div>
        )}

        {/* Step 3: Done */}
        {step === 3 && result && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Flow Oluşturuldu!</h3>
            <p style={{ color: 'var(--text3)', fontSize: 13, marginBottom: 20 }}>
              <b>{result.steps_count}</b> node ile flow hazır.<br/>
              Panelde Flow Editörden açıp düzenleyebilirsin.
            </p>
            <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: 16, marginBottom: 20, textAlign: 'left' }}>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>Oluşturulan Flow:</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{result.flow?.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{result.steps_count} node · Bot: {bot.name}</div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => { setStep(1); setDescription(''); setImages([]); setResult(null); }}>
                🔄 Yeni Flow
              </button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={onClose}>
                ✏️ Flow Editörde Aç
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
